/**
 * Бейдж «отвалился» статуса слота roster (Фаза 15, A2; 15.1 — вынесен из
 * LobbyRoster в общий компонент, переиспользуется живым рейтингом
 * LiveLeaderboard). Рендерится только ведущим (host-панели); игроку статусная
 * разметка не показывается (у него свой экран реконнекта).
 *
 * Tooltip времени живёт НЕ на бейдже, а на строке-контейнере (li) — его
 * собирает хук useDroppedTooltip: история входов без IP (приватность), время
 * отвала, при возврате — дополняется временем возврата.
 *
 * data-testid-контракт (гейты прода и e2e-демо): roster-status-dropped.
 */
import { useTranslation } from 'react-i18next';
import type { RosterEntry } from '../protocol';

/** Локаль i18n ('ru'|'en'|'cn') → BCP-47 тег для Intl-форматирования времени. */
const INTL_LOCALE: Record<string, string> = { ru: 'ru-RU', en: 'en', cn: 'zh-CN' };

/** Unix-мс → локализованные дата+время (tooltip строки, A2). */
export function formatTimestamp(ts: number, language: string): string {
  return new Date(ts).toLocaleString(INTL_LOCALE[language] ?? language, {
    dateStyle: 'short',
    timeStyle: 'medium',
  });
}

/**
 * Tooltip строки слота для ведущего: «отвалился: <время>», при возврате —
 * « · вернулся: <время>». undefined — отвала не было (tooltip не нужен).
 * Вызов безусловный (rules of hooks); применять к строке — только в host-панели.
 */
export function useDroppedTooltip(entry: RosterEntry): string | undefined {
  const { t, i18n } = useTranslation();
  if (entry.status !== 'dropped' || !entry.droppedAt) return undefined;
  const parts = [
    t('roster.droppedAt', { time: formatTimestamp(entry.droppedAt, i18n.language) }),
  ];
  if (entry.returnedAt) {
    parts.push(t('roster.returnedAt', { time: formatTimestamp(entry.returnedAt, i18n.language) }));
  }
  return parts.join(' · ');
}

/** Капсула «отвалился» — серо-янтарная, по образцу .roster-locked. */
export function DroppedBadge(): JSX.Element {
  const { t } = useTranslation();
  return (
    <span className="badge roster-dropped" data-testid="roster-status-dropped">
      {t('roster.status.dropped')}
    </span>
  );
}
